import searchAtomPair, { SearchParams } from 'atoms/search-atom';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import type { Geometry } from 'geojson';
import type { MapRef } from 'react-map-gl';
import { createResourceCacheMapAtom } from 'lib/atoms';
import { getMapDefaultConfig } from 'components/map/atoms';

export interface AreasQueryResponse {
  id: number;
  businessId: string;
  href: string;
  name: string;
  displayName: string;
  category: string;
  grossAcres: number;
  shared: boolean;
  active: boolean;
  createdBy: string;
  updatedBy: string | null;
  dataSource: string;
  purpose: string;
  createdAt: string;
  updatedAt: string;
}

export interface AreaGeometriesQueryParams extends SearchParams {
  text?: string;
  category?: string;
  data_source?: string;
  created_by?: string;
  date_filter_type?: 'none' | 'created' | 'updated';
  purpose?: string;
  created_at?: {
    start?: string;
    end?: string;
  };
  updated_at?: {
    start?: string;
    end?: string;
  };
  active?: boolean;
  shared?: boolean;
}

/** Facet buckets returned alongside the catalog results. */
export interface AreaGeometriesFacets {
  category?: { buckets: FacetBucket[] };
  dataSource?: { buckets: FacetBucket[] };
  createdBy?: { buckets: FacetBucket[] };
}

export interface FacetBucket {
  id: string;
  value: string;
  count: number;
}

/**
 * Search-atom pair for the Areas catalog. Params are owned by the page and passed
 * on every refetch, so the atom holds no defaults of its own.
 *
 * @return {Array} The search atom and its setter.
 */
export const createAreasCatalogSearchAtom = () =>
  searchAtomPair<
    AreasQueryResponse,
    AreaGeometriesQueryParams,
    AreaGeometriesFacets
  >({ href: '/queries/area_geometries' });

/**
 * Per-area geometry, cached by href. The catalog payload carries no geometry, so
 * the map fetches each area by id — and the cache is what stops a re-ticked row
 * fetching the same shape again.
 */
export interface AreaGeometryResource {
  id: number;
  geometry: Geometry;
  /** Set only on areas that were given a colour; null on GIS-imported rows. */
  displayColor: string | null;
}

export const areaGeometryCacheAtom =
  createResourceCacheMapAtom<AreaGeometryResource>();

/**
 * The catalog map's persisted view settings, and a handle on the live map. The
 * key is versioned because scroll zoom moved on by default — a stored config
 * from before that wins over the default and would keep the wheel dead.
 */
export const catalogMapConfigAtom = atomWithStorage(
  'insights-areas-catalog-map-config-v2',
  getMapDefaultConfig({ scrollZoom: { checked: true } })
);

export const catalogMapRefAtom = atom<MapRef | null>(null);
